import type {
  TenantSeedTheme,
  TenantTheme,
  TenantDarkTheme,
  TenantColors
} from './types'

/**
 * Parses "H S% L%" string into { h, s, l } numbers.
 */
function parseHSL(value: string): { h: number; s: number; l: number } {
  const parts = value.replaceAll('%', '').split(/\s+/).map(Number)
  return { h: parts[0], s: parts[1], l: parts[2] }
}

/**
 * Formats h, s, l numbers back to "H S% L%" string.
 */
function formatHSL(h: number, s: number, l: number): string {
  return `${Math.round(h)} ${Math.round(s)}% ${Math.round(l)}%`
}

/**
 * Converts HSL to hex color string.
 */
function hslToHex(h: number, s: number, l: number): string {
  const sNorm = s / 100
  const lNorm = l / 100
  const a = sNorm * Math.min(lNorm, 1 - lNorm)
  const f = (n: number) => {
    const k = (n + h / 30) % 12
    const color = lNorm - a * Math.max(Math.min(k - 3, 9 - k, 1), -1)
    return Math.round(255 * color)
      .toString(16)
      .padStart(2, '0')
  }
  return `#${f(0)}${f(8)}${f(4)}`
}

/**
 * Generates the full light theme from seed primary + secondary HSL values.
 */
export function generateTheme(seed: TenantSeedTheme): TenantTheme {
  const p = parseHSL(seed.primary)

  return {
    primary: seed.primary,
    primaryForeground: '0 0% 100%',
    secondary: seed.secondary,
    secondaryForeground: '0 0% 100%',
    accent: seed.primary,
    accentForeground: '0 0% 100%',
    neonPrimary: seed.primary,
    background: '0 0% 98%',
    foreground: formatHSL(p.h, 15, 20),
    card: '0 0% 100%',
    cardForeground: formatHSL(p.h, 15, 20),
    popover: '0 0% 100%',
    popoverForeground: formatHSL(p.h, 15, 20),
    muted: formatHSL(p.h, 15, 96),
    mutedForeground: formatHSL(p.h, 10, 50),
    border: formatHSL(p.h, 15, 90),
    input: formatHSL(p.h, 15, 94),
    ring: formatHSL(p.h, Math.round(p.s * 0.65), Math.round(p.l * 0.58)),
    destructive: '0 72% 51%',
    destructiveForeground: '0 0% 100%',
    gradientPrimaryFrom: seed.primary,
    gradientPrimaryTo: seed.secondary,
    sidebarBackground: formatHSL(p.h, 20, 98),
    sidebarForeground: formatHSL(p.h, 15, 25),
    sidebarPrimary: formatHSL(p.h, 85, 60),
    sidebarPrimaryForeground: '0 0% 100%',
    sidebarAccent: formatHSL(p.h, 30, 94),
    sidebarAccentForeground: formatHSL(p.h, 15, 25),
    sidebarBorder: formatHSL(p.h, 20, 88),
    sidebarRing: formatHSL(p.h, 85, 60),
    kycBackground: formatHSL(p.h + 30, 23, 94),
    kycText: formatHSL(p.h + 25, 58, 10),
    emailPrimaryHex: seed.emailPrimaryHex,
    emailSecondaryHex: seed.emailSecondaryHex,
    emailBgHex: hslToHex(p.h, 15, 97),
    emailFooterBgHex: hslToHex(p.h, 20, 94)
  }
}

/**
 * Generates the full dark theme from seed primary + secondary HSL values.
 */
export function generateDarkTheme(seed: TenantSeedTheme): TenantDarkTheme {
  const p = parseHSL(seed.primary)

  return {
    background: formatHSL(p.h, 30, 8),
    foreground: formatHSL(p.h - 225 < 0 ? p.h + 135 : p.h - 225, 60, 95),
    card: formatHSL(p.h, 25, 10),
    cardForeground: formatHSL(p.h - 225 < 0 ? p.h + 135 : p.h - 225, 60, 95),
    popover: formatHSL(p.h, 25, 10),
    popoverForeground: formatHSL(p.h - 225 < 0 ? p.h + 135 : p.h - 225, 60, 95),
    primary: seed.primary,
    primaryForeground: '0 0% 100%',
    secondary: seed.secondary,
    secondaryForeground: '0 0% 100%',
    muted: formatHSL(p.h, 20, 15),
    mutedForeground: formatHSL(p.h, 10, 65),
    accent: seed.primary,
    accentForeground: '0 0% 100%',
    neonPrimary: seed.primary,
    kycBackground: formatHSL(p.h + 30, 23, 94),
    kycText: formatHSL(p.h + 25, 58, 10),
    destructive: '0 75% 60%',
    destructiveForeground: formatHSL(
      p.h - 225 < 0 ? p.h + 135 : p.h - 225,
      60,
      95
    ),
    border: formatHSL(p.h, 20, 18),
    input: formatHSL(p.h, 20, 18),
    ring: formatHSL(p.h, Math.round(p.s * 0.65), Math.round(p.l * 0.72)),
    sidebarBackground: formatHSL(p.h, 30, 8),
    sidebarForeground: formatHSL(p.h - 225 < 0 ? p.h + 135 : p.h - 225, 60, 90),
    sidebarPrimary: formatHSL(p.h, 85, 70),
    sidebarPrimaryForeground: formatHSL(p.h, 30, 8),
    sidebarAccent: formatHSL(p.h, 25, 15),
    sidebarAccentForeground: formatHSL(
      p.h - 225 < 0 ? p.h + 135 : p.h - 225,
      60,
      90
    ),
    sidebarBorder: formatHSL(p.h, 20, 18),
    sidebarRing: formatHSL(p.h, 85, 70)
  }
}

/**
 * Generates all app colors (SCSS variable replacements) from primary hex.
 * Only primary/secondary hex needed — everything else is derived.
 */
export function generateColors(
  primaryHex: string,
  secondaryHex: string
): TenantColors {
  return {
    textColorPrimary: primaryHex,
    textColorSecondary: darken(primaryHex, 30),
    textColorLighter: lighten(primaryHex, 30),
    textColorLighter2: lighten(primaryHex, 10),
    textColorLighter3: desaturate(lighten(primaryHex, 15), 20),
    textColorHeading: darken(primaryHex, 15),
    textColorContent: lighten(primaryHex, 20),
    textColorLight: '#464f60',
    textColorHelper: lighten(primaryHex, 5),
    textLightGrey: '#cbd1ea',
    textDarkGrey: '#a0aec3',
    textColorGreen: '#57e777',
    textColorGreen2: '#48a56c',
    textColorRed: '#aa5558',
    buttonColorPrimary: secondaryHex,
    buttonColorPrimaryGradientStart: lighten(secondaryHex, 10),
    buttonBackgroundColorPrimary: primaryHex,
    buttonBackgroundColorSubtle: '#f2f4f6',
    buttonBackgroundColorLighter: '#eef1f5',
    buttonColorSubtle: '#778ca2',
    buttonColorPrimaryDefault: '#eaedf0',
    buttonBorderColor: '#dde1eb',
    backgroundColorWhite: '#fff',
    backgroundColorLighter: '#f8fafb',
    backgroundColorLighter200: '#f3f6f8',
    backgroundColorDarker: '#e9edf5',
    backgroundColorDarker2: '#cdd5e4',
    backgroundColorDarker3: '#e1e5ed',
    backgroundColorLighterRed: '#f9ecee',
    lightRedBackgroundColor: '#ffb4b4',
    darkRedTextColor: '#b22e2c',
    lightGreenBackgroundColor: '#96fab3',
    darkGreenTextColor: '#1d843c',
    dividerColor: lighten(primaryHex, 45),
    borderColor: '#eaecf0',
    borderColorDarker: `${primaryHex}3d`,
    sideNavShadowColor: '#e6eaef',
    toolbarBorderBottomColor: '#f7f7fa',
    dropdownDividerColor: '#f2f4f6'
  }
}

// --- Hex color manipulation helpers ---

function hexToRgb(hex: string): { r: number; g: number; b: number } {
  const clean = hex.replace('#', '')
  return {
    r: Number.parseInt(clean.substring(0, 2), 16),
    g: Number.parseInt(clean.substring(2, 4), 16),
    b: Number.parseInt(clean.substring(4, 6), 16)
  }
}

function rgbToHsl(
  r: number,
  g: number,
  b: number
): { h: number; s: number; l: number } {
  const rNorm = r / 255
  const gNorm = g / 255
  const bNorm = b / 255
  const max = Math.max(rNorm, gNorm, bNorm)
  const min = Math.min(rNorm, gNorm, bNorm)
  const l = (max + min) / 2
  let h = 0
  let sVal = 0

  if (max !== min) {
    const d = max - min
    sVal = l > 0.5 ? d / (2 - max - min) : d / (max + min)
    if (max === rNorm) h = ((gNorm - bNorm) / d + (gNorm < bNorm ? 6 : 0)) / 6
    else if (max === gNorm) h = ((bNorm - rNorm) / d + 2) / 6
    else h = ((rNorm - gNorm) / d + 4) / 6
  }

  return { h: h * 360, s: sVal * 100, l: l * 100 }
}

function hslToRgb(
  h: number,
  s: number,
  l: number
): { r: number; g: number; b: number } {
  const sNorm = s / 100
  const lNorm = l / 100
  const a = sNorm * Math.min(lNorm, 1 - lNorm)
  const f = (n: number) => {
    const k = (n + h / 30) % 12
    return Math.round(
      255 * (lNorm - a * Math.max(Math.min(k - 3, 9 - k, 1), -1))
    )
  }
  return { r: f(0), g: f(8), b: f(4) }
}

function rgbToHex(r: number, g: number, b: number): string {
  const toHex = (n: number) =>
    Math.max(0, Math.min(255, Math.round(n)))
      .toString(16)
      .padStart(2, '0')
  return `#${toHex(r)}${toHex(g)}${toHex(b)}`
}

function lighten(hex: string, amount: number): string {
  const { r, g, b } = hexToRgb(hex)
  const { h, s, l } = rgbToHsl(r, g, b)
  const newL = Math.min(100, l + amount)
  const rgb = hslToRgb(h, s, newL)
  return rgbToHex(rgb.r, rgb.g, rgb.b)
}

function darken(hex: string, amount: number): string {
  const { r, g, b } = hexToRgb(hex)
  const { h, s, l } = rgbToHsl(r, g, b)
  const newL = Math.max(0, l - amount)
  const rgb = hslToRgb(h, s, newL)
  return rgbToHex(rgb.r, rgb.g, rgb.b)
}

function desaturate(hex: string, amount: number): string {
  const { r, g, b } = hexToRgb(hex)
  const { h, s, l } = rgbToHsl(r, g, b)
  const newS = Math.max(0, s - amount)
  const rgb = hslToRgb(h, newS, l)
  return rgbToHex(rgb.r, rgb.g, rgb.b)
}
